/**
 * Template Utilities
 * 
 * Utility functions for template processing
 */

/**
 * Map standard field names to their Shopify API equivalents
 * This helps users use more intuitive field names in templates
 */
export const getStandardFieldMapping = (field) => {
  const fieldMappings = {
    'description': 'body_html',
    'desc': 'body_html',
    'body': 'body_html',
    'body_html': 'body_html',
    'descriptionHtml': 'body_html'
  };
  
  return fieldMappings[field] || field;
};

/**
 * Get field value from product data
 * @param {Object} product - Product data
 * @param {string} field - Field name
 * @returns {*} Field value
 */
export const getFieldValue = (product, field) => {
  if (!product) return undefined;
  
  const productId = product.id.toString();
  
  // Map standard field names to Shopify API field names
  const mappedField = getStandardFieldMapping(field);
  
  // Debug info for metafields and mapped fields
  if (field.includes('.') || field !== mappedField) {
    console.log(`DEBUG getFieldValue for field ${field} (mapped to ${mappedField}):`, {
      productId,
      originalField: field,
      mappedField,
      hasWindowObject: typeof window !== 'undefined',
      hasLocalEdits: typeof window !== 'undefined' && !!window.localEdits,
      hasEditHistory: typeof window !== 'undefined' && !!window.editHistory,
      hasProductEdits: typeof window !== 'undefined' && !!window.productEdits,
      productInLocalEdits: typeof window !== 'undefined' && window.localEdits && !!window.localEdits[productId],
      productInEditHistory: typeof window !== 'undefined' && window.editHistory && window.editHistory.present && !!window.editHistory.present[productId],
      productInProductEdits: typeof window !== 'undefined' && window.productEdits && !!window.productEdits[productId]
    });
  }
  
  // Check all possible data sources in priority order
  const editHistoryValue = window.editHistory?.present?.[productId]?.[mappedField];
  const localEditsValue = window.localEdits?.[productId]?.[mappedField];
  const productEditsValue = window.productEdits?.[productId]?.[mappedField];
  
  console.log(`Getting field value for ${mappedField}:`, {
    productId,
    editHistoryValue,
    localEditsValue,
    productEditsValue,
    originalValue: product[mappedField]
  });
  
  // Special handling for tags field
  if (field === 'tags') {
    const tags = editHistoryValue ?? localEditsValue ?? productEditsValue ?? product.tags;
    return Array.isArray(tags) ? tags.join(', ') : tags;
  }

  // Handle metafield notation (e.g., 'metafield.seo.keywords')
  if (field.startsWith('metafield.')) {
    const parts = field.split('.');
    if (parts.length >= 3) {
      const namespace = parts[1];
      const key = parts[2];
      const metafieldKey = `${namespace}.${key}`;
      
      console.log(`DEBUG: Looking for metafield ${metafieldKey} with metafield. notation`);
      
      // Check if metafields exist in the product object
      if (product.metafields && typeof product.metafields === 'object' && !Array.isArray(product.metafields)) {
        // If metafields is an object with namespace.key format
        if (product.metafields[metafieldKey] !== undefined) {
          const value = typeof product.metafields[metafieldKey] === 'object' ? 
            product.metafields[metafieldKey].value : 
            product.metafields[metafieldKey];
            
          console.log(`Found metafield ${metafieldKey} in product.metafields with value:`, value);
          return value;
        }
      }
      
      // Check localEdits first for the most current value
      if (typeof window !== 'undefined' && window.localEdits && window.localEdits[productId]) {
        // Check metafields object
        if (window.localEdits[productId].metafields && 
            window.localEdits[productId].metafields[metafieldKey]) {
          const value = window.localEdits[productId].metafields[metafieldKey].value || 
                      window.localEdits[productId].metafields[metafieldKey];
          if (value !== undefined) {
            console.log(`Found metafield ${metafieldKey} in window.localEdits.metafields with value:`, value);
            return value;
          }
        }
        
        // Check metafield_updates
        if (window.localEdits[productId].metafield_updates && 
            window.localEdits[productId].metafield_updates[metafieldKey] !== undefined) {
          const value = window.localEdits[productId].metafield_updates[metafieldKey];
          console.log(`Found metafield ${metafieldKey} in window.localEdits.metafield_updates with value:`, value);
          return value;
        }
        
        // Check metafields_array
        if (window.localEdits[productId].metafields_array && 
            Array.isArray(window.localEdits[productId].metafields_array)) {
          const metafieldInArray = window.localEdits[productId].metafields_array.find(
            m => m.namespace === namespace && m.key === key
          );
          
          if (metafieldInArray && metafieldInArray.value !== undefined) {
            console.log(`Found metafield ${metafieldKey} in window.localEdits.metafields_array with value:`, metafieldInArray.value);
            return metafieldInArray.value;
          }
        }
      }
      
      // Check the metafields array in the product object
      if (Array.isArray(product.metafields)) {
        const metafield = product.metafields.find(m => 
          m.namespace === namespace && m.key === key
        );
        
        if (metafield && metafield.value !== undefined) {
          console.log(`Found metafield ${metafieldKey} in product.metafields array with value:`, metafield.value);
          return metafield.value;
        }
      }
      
      // Check if there's a direct property with namespace.key format
      if (product[namespace] && product[namespace][key] !== undefined) {
        console.log(`Found metafield as direct property ${namespace}.${key} with value:`, product[namespace][key]);
        return product[namespace][key];
      }
      
      // Check if there's a direct property with metafieldKey format
      if (product[metafieldKey] !== undefined) {
        console.log(`Found metafield as direct property ${metafieldKey} with value:`, product[metafieldKey]);
        return product[metafieldKey];
      }
      
      console.log(`Metafield ${metafieldKey} not found in any source`);
      return '';
    }
  }
  
  // Handle direct namespace.key notation (e.g., 'custom.googlekeywords')
  if (field.includes('.') && !field.startsWith('metafields.') && !field.startsWith('metafield.')) {
    const [namespace, key] = field.split('.');
    const metafieldKey = `${namespace}.${key}`;
    
    console.log(`DEBUG: Looking for direct metafield ${metafieldKey}`);
    
    // Check if metafields exist in the product object
    if (product.metafields && typeof product.metafields === 'object' && !Array.isArray(product.metafields)) {
      // If metafields is an object with namespace.key format
      if (product.metafields[metafieldKey] !== undefined) {
        const value = typeof product.metafields[metafieldKey] === 'object' ? 
          product.metafields[metafieldKey].value : 
          product.metafields[metafieldKey];
          
        console.log(`Found direct metafield ${metafieldKey} in product.metafields with value:`, value);
        return value;
      }
    }
    
    // Check localEdits first for the most current value
    if (typeof window !== 'undefined' && window.localEdits && window.localEdits[productId]) {
      // Check metafields object
      if (window.localEdits[productId].metafields && 
          window.localEdits[productId].metafields[metafieldKey]) {
        const value = window.localEdits[productId].metafields[metafieldKey].value || 
                    window.localEdits[productId].metafields[metafieldKey];
        if (value !== undefined) {
          console.log(`Found direct metafield ${metafieldKey} in window.localEdits.metafields with value:`, value);
          return value;
        }
      }
      
      // Check metafield_updates
      if (window.localEdits[productId].metafield_updates && 
          window.localEdits[productId].metafield_updates[metafieldKey] !== undefined) {
        const value = window.localEdits[productId].metafield_updates[metafieldKey];
        console.log(`Found direct metafield ${metafieldKey} in window.localEdits.metafield_updates with value:`, value);
        return value;
      }
      
      // Check metafields_array
      if (window.localEdits[productId].metafields_array && 
          Array.isArray(window.localEdits[productId].metafields_array)) {
        const metafieldInArray = window.localEdits[productId].metafields_array.find(
          m => m.namespace === namespace && m.key === key
        );
        
        if (metafieldInArray && metafieldInArray.value !== undefined) {
          console.log(`Found direct metafield ${metafieldKey} in window.localEdits.metafields_array with value:`, metafieldInArray.value);
          return metafieldInArray.value;
        }
      }
    }
    
    // Check the metafields array in the product object
    if (Array.isArray(product.metafields)) {
      const metafield = product.metafields.find(m => 
        m.namespace === namespace && m.key === key
      );
      
      if (metafield && metafield.value !== undefined) {
        console.log(`Found direct metafield ${metafieldKey} in product.metafields array with value:`, metafield.value);
        return metafield.value;
      }
    }
    
    // Check if there's a direct property with namespace.key format
    if (product[namespace] && product[namespace][key] !== undefined) {
      console.log(`Found direct metafield as property ${namespace}.${key} with value:`, product[namespace][key]);
      return product[namespace][key];
    }
    
    console.log(`Direct metafield ${metafieldKey} not found in any source`);
    return '';
  }
  
  // Handle basic fields
  if (field === 'title' || field === 'handle' || field === 'vendor' || 
      field === 'tags' || field === 'id') {
    return editHistoryValue ?? localEditsValue ?? productEditsValue ?? product[mappedField];
  }
  
  // Handle product_type (may be stored as productType)
  if (field === 'product_type' || field === 'productType') {
    return (editHistoryValue ?? localEditsValue ?? productEditsValue ?? product.product_type) || product.productType;
  }
  
  // Handle description (may be stored as body_html or descriptionHtml)
  if (field === 'description' || field === 'body_html' || field === 'descriptionHtml') {
    return (editHistoryValue ?? localEditsValue ?? productEditsValue ?? product.body_html) || product.descriptionHtml || product.description;
  }
  
  // Handle option fields
  if (field.startsWith('option') && field.includes('_')) {
    const [optionBase, optionPart] = field.split('_');
    
    // Check for direct field in edits first
    const directEditValue = editHistoryValue ?? localEditsValue ?? productEditsValue;
    if (directEditValue !== undefined) {
      return directEditValue;
    }
    
    // Extract option index (1-based in field name, 0-based in array)
    const optionIndex = parseInt(optionBase.replace('option', '')) - 1;
    
    // Get the option data
    if (product.options && product.options[optionIndex]) {
      if (optionPart === 'name') {
        return product.options[optionIndex].name;
      } else if (optionPart === 'values') {
        return product.options[optionIndex].values;
      }
    }
    
    // If we didn't find it in the options array, check for direct properties
    return product[field];
  }
  
  // For all other fields, return the edited value or fall back to the original
  return editHistoryValue ?? localEditsValue ?? productEditsValue ?? product[mappedField];
};

/**
 * Filter template result based on output fields
 * @param {Object} result - Template result
 * @param {Array} outputFields - Output fields
 * @returns {Object} Filtered result
 */
export const filterResultByOutputFields = (result, outputFields) => {
  if (!outputFields || !Array.isArray(outputFields) || outputFields.length === 0) {
    return result; // No filtering needed
  }
  
  const filteredResult = {};
  
  // Process each field in the result
  for (const [field, value] of Object.entries(result)) {
    // Check if this field is in the outputFields
    const isOutputField = outputFields.some(outputField => {
      // Direct field match
      if (outputField === field) {
        return true;
      }
      
      // Handle option fields (option1_name, option1_values, etc.)
      if (field.startsWith('option') && field.includes('_')) {
        const optionBase = field.split('_')[0]; // e.g., "option1"
        if (outputField === optionBase || outputField === 'options') {
          return true;
        }
      }
      
      // Handle metafield notation
      if ((field.startsWith('metafield.') || field.startsWith('metafields.') || field.startsWith('custom.')) && 
          (outputField === 'metafields' || outputField === field)) {
        return true;
      }
      
      return false;
    });
    
    // Special case for options which should only be included when option fields are requested
    const isSpecialField = (field === 'options' && outputFields.some(f => 
                          f.startsWith('option') || f === 'options')) || 
                         (field === 'variants' && outputFields.some(f => 
                          f.startsWith('variants.')));
    
    if (isOutputField || isSpecialField) {
      console.log(`Including field "${field}" in filtered results`);
      filteredResult[field] = value;
    } else {
      console.log(`Excluding field "${field}" from filtered results (not in output fields)`);
    }
  }
  
  return filteredResult;
};

/**
 * Generate template prompt for OpenAI
 * @param {Object} template - Template configuration
 * @param {Object} productData - Product data
 * @returns {string} Template prompt
 */
export const generateTemplatePrompt = (template, productData) => {
  // Start with the template prompt
  let prompt = template.prompt || '';
  
  // Add product data to the prompt
  prompt += `\n\nProduct Data:\n${JSON.stringify(productData, null, 2)}`;
  
  // Add output format instructions
  prompt += `\n\nPlease generate content for the following fields: ${template.outputFields.join(', ')}`;
  prompt += `\n\nRespond with a valid JSON object containing only the requested fields.`;
  
  return prompt;
};

/**
 * Parse template result from OpenAI response
 * @param {Object} response - OpenAI response
 * @returns {Object} Parsed template result
 */
export const parseTemplateResult = (response) => {
  try {
    // Extract the content from the response
    const content = response.choices[0].message.content;
    
    // Try to parse the JSON
    const jsonMatch = content.match(/```json\n([\s\S]*?)\n```/) || 
                     content.match(/```\n([\s\S]*?)\n```/) || 
                     content.match(/{[\s\S]*?}/);
    
    if (jsonMatch) {
      // Parse the JSON
      return JSON.parse(jsonMatch[0].replace(/```json\n|```\n|```/g, ''));
    } else {
      // Try to parse the whole content as JSON
      return JSON.parse(content);
    }
  } catch (error) {
    console.error('Error parsing template result:', error);
    throw new Error(`Failed to parse template result: ${error.message}`);
  }
};
